import { useTranslation } from '@/lib/i18n';
import { PRODUCT_LOCALES, type ProductLocale } from '../schemas/productSchema';

/**
 * mall-visibility 파트1 — 상품 노출 국가(몰) 다중선택.
 *
 * 고도몰처럼 상품별 노출 국가가 상이(제공사 계약). 독립몰 4개(한국/일본/영문/중문)에서
 * 어느 몰에 노출할지 운영자가 선택한다. **기본=전체노출**(전 몰 선택). 한 몰이라도 해제하면
 * 그 몰 프론트에서 숨겨진다. 비노출 PDP 직접 진입은 SF 가 상품 리스트로 redirect(fe-cat).
 *
 * 표시는 친화 라벨(한국몰/일본몰/영문몰/중문몰)만 — 내부 코드(ko-KR 등)는 노출하지 않는다.
 *
 * 값 모델(폼): `visibleMalls: ProductLocale[]` — 노출 대상 몰 코드 배열. 빈 배열이 아닌,
 * **선택된 몰의 집합**을 보유한다. 전체노출은 4개 전부 선택된 상태로 표현(detailMapper 가
 * 전체노출 ↔ 와이어 규칙으로 변환). be-cat 계약에 따라 와이어 필드명/전체노출 표현은
 * detailMapper 에서만 결정되며, 본 컴포넌트는 UI(선택 집합)만 담당한다.
 */

export interface ProductVisibleMallsFieldProps {
  /** 현재 노출 대상 몰 코드 집합. */
  value: ReadonlyArray<ProductLocale>;
  disabled?: boolean;
  onChange: (next: ProductLocale[]) => void;
}

// 몰 코드 → i18n 라벨 키. 친화 라벨만 노출(내부 코드 표시 금지).
const MALL_LABEL_KEY: Record<ProductLocale, string> = {
  'ko-KR': 'products.fields.visibleMalls.ko',
  'ja-JP': 'products.fields.visibleMalls.ja',
  'en-US': 'products.fields.visibleMalls.en',
  'zh-CN': 'products.fields.visibleMalls.zh',
};

const MALL_LABEL_FALLBACK: Record<ProductLocale, string> = {
  'ko-KR': '한국몰',
  'ja-JP': '일본몰',
  'en-US': '영문몰',
  'zh-CN': '중문몰',
};

export const ProductVisibleMallsField = ({
  value,
  disabled,
  onChange,
}: ProductVisibleMallsFieldProps) => {
  const { t } = useTranslation('common');
  const selected = new Set(value);
  const allSelected = PRODUCT_LOCALES.every((m) => selected.has(m));

  const toggle = (mall: ProductLocale, checked: boolean) => {
    // 선택 순서를 PRODUCT_LOCALES 표준 순서로 정규화 — 페이로드 안정성(byte 일관) 확보.
    const nextSet = new Set(selected);
    if (checked) nextSet.add(mall);
    else nextSet.delete(mall);
    onChange(PRODUCT_LOCALES.filter((m) => nextSet.has(m)));
  };

  const selectAll = () => onChange([...PRODUCT_LOCALES]);

  return (
    <div className="flex flex-col gap-2" data-testid="product-visible-malls-field">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {PRODUCT_LOCALES.map((mall) => (
          <label
            key={mall}
            className="inline-flex items-center gap-2 text-[14px]"
            style={{ color: 'var(--content-default)' }}
          >
            <input
              type="checkbox"
              checked={selected.has(mall)}
              disabled={disabled}
              onChange={(e) => toggle(mall, e.target.checked)}
              aria-label={t(MALL_LABEL_KEY[mall], { defaultValue: MALL_LABEL_FALLBACK[mall] })}
            />
            {t(MALL_LABEL_KEY[mall], { defaultValue: MALL_LABEL_FALLBACK[mall] })}
          </label>
        ))}
      </div>
      <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--content-muted)' }}>
        {allSelected ? (
          <span>{t('products.fields.visibleMalls.allHint', { defaultValue: '전체 노출 (모든 국가몰에 노출)' })}</span>
        ) : selected.size === 0 ? (
          <span style={{ color: 'var(--signal-danger)' }}>
            {t('products.fields.visibleMalls.noneHint', { defaultValue: '선택된 몰이 없습니다. 최소 1개 국가몰을 선택하세요.' })}
          </span>
        ) : (
          <button
            type="button"
            className="underline"
            disabled={disabled}
            onClick={selectAll}
            style={{ color: 'var(--accent-default)' }}
          >
            {t('products.fields.visibleMalls.selectAll', { defaultValue: '전체 노출로 되돌리기' })}
          </button>
        )}
      </div>
    </div>
  );
};
